import { Action, ActionReducer, MetaReducer } from '@ngrx/store';
import { AppState } from '../../state/app.state';

const STORAGE_PREFIX = 'apidef.preference.';
const LEGACY_SENSITIVE_KEYS = [
  'auth',
  'user',
  'tryIt',
  'tryItRequests',
  'organizations',
  'projects',
  'specCheckResults',
  'markdownCheckResults',
  'organizationUsers',
  'organizationInvitations',
  'organizationApiKeys',
  'organizationSupportReports',
] as const;

type PersistedPreferences = Pick<AppState, 'theme' | 'sideMenuVisibility'>;

function getLocalStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function purgeLegacySensitiveStorage(storage: Storage | null = getLocalStorage()): void {
  if (!storage) {
    return;
  }

  for (const key of LEGACY_SENSITIVE_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      // A denied storage operation must not prevent the application from starting.
    }
  }
}

function readPreference<K extends keyof PersistedPreferences>(
  key: K,
  storage: Storage,
): PersistedPreferences[K] | undefined {
  try {
    const value = storage.getItem(`${STORAGE_PREFIX}${key}`);
    return value === null ? undefined : JSON.parse(value) as PersistedPreferences[K];
  } catch {
    return undefined;
  }
}

function writePreference<K extends keyof PersistedPreferences>(
  key: K,
  value: PersistedPreferences[K],
  storage: Storage,
): void {
  try {
    storage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value));
  } catch {
    // Preferences are best-effort and must not break state updates.
  }
}

export function safeStorePersistenceMetaReducer(
  reducer: ActionReducer<AppState>,
): ActionReducer<AppState> {
  return (state: AppState | undefined, action: Action): AppState => {
    const storage = getLocalStorage();
    let hydratedState = state;

    if (state === undefined && storage) {
      const initialState = reducer(undefined, { type: '@apidef/persistence/init' });
      hydratedState = {
        ...initialState,
        theme: readPreference('theme', storage) ?? initialState.theme,
        sideMenuVisibility:
          readPreference('sideMenuVisibility', storage) ?? initialState.sideMenuVisibility,
      };
    }

    const nextState = reducer(hydratedState, action);
    if (storage) {
      writePreference('theme', nextState.theme, storage);
      writePreference('sideMenuVisibility', nextState.sideMenuVisibility, storage);
    }
    return nextState;
  };
}

export const safeStoreMetaReducers: MetaReducer<AppState>[] = [
  safeStorePersistenceMetaReducer,
];
